import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { ExerciseService } from '../../services/exerciseService';
import { Exercise, ExerciseHistoryEntry } from '../../services/types';

interface ExercisePickerDialogProps {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Pre-selects the muscle group filter when the dialog opens. */
  initialMuscleGroup?: string;
  onConfirm: (exercise: Exercise) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Reusable exercise picker with search + muscle group / equipment / movement
 * pattern filters and history-aware ordering. Shared between the workout logger
 * (substitute) and the workout planner (substitute / add) so both screens offer
 * the same way of finding the right exercise.
 */
const ExercisePickerDialog: React.FC<ExercisePickerDialogProps> = ({
  open,
  title,
  description,
  confirmLabel,
  initialMuscleGroup,
  onConfirm,
  onClose,
}) => {
  const [muscleGroups, setMuscleGroups] = useState<string[]>([]);
  const [equipmentOptions, setEquipmentOptions] = useState<string[]>([]);
  const [movementPatterns, setMovementPatterns] = useState<string[]>([]);
  const [history, setHistory] = useState<ExerciseHistoryEntry[]>([]);
  const [results, setResults] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const [search, setSearch] = useState('');
  const [filterMuscle, setFilterMuscle] = useState('');
  const [filterEquipment, setFilterEquipment] = useState('');
  const [filterMovementPattern, setFilterMovementPattern] = useState('');
  const [selected, setSelected] = useState<Exercise | null>(null);

  // Load filter option lists + history and reset state whenever the dialog opens.
  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    setSearch('');
    setFilterMuscle(initialMuscleGroup || '');
    setFilterEquipment('');
    setFilterMovementPattern('');
    setSelected(null);

    (async () => {
      try {
        setLoading(true);
        const [mg, eq, mp, hist] = await Promise.all([
          ExerciseService.getMuscleGroups(),
          ExerciseService.getEquipmentTypes(),
          ExerciseService.getMovementPatterns(),
          ExerciseService.getExercisesWithHistory(),
        ]);
        if (cancelled) return;
        setMuscleGroups(mg);
        setEquipmentOptions(eq);
        setMovementPatterns(mp);
        setHistory(hist);
      } catch (err) {
        console.error('Failed to load exercise picker data', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, initialMuscleGroup]);

  // Debounced server-side search whenever the query or filters change.
  useEffect(() => {
    if (!open) return;

    const timeout = window.setTimeout(async () => {
      try {
        setLoading(true);
        const result = await ExerciseService.searchExercises({
          q: search || undefined,
          muscleGroup: filterMuscle || undefined,
          equipment: filterEquipment || undefined,
          movementPattern: filterMovementPattern || undefined,
          limit: 100,
        });
        setResults(result.exercises);
      } catch (err) {
        console.error('Failed to search exercises', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [open, search, filterMuscle, filterEquipment, filterMovementPattern]);

  const historyMap = useMemo(
    () => new Map(history.map((entry) => [entry.id, entry])),
    [history],
  );

  // Exercises with history first (most recent on top), then alphabetical.
  const orderedExercises = useMemo(() => {
    return [...results].sort((left, right) => {
      const leftHistory = historyMap.get(left.id);
      const rightHistory = historyMap.get(right.id);
      const leftHasHistory = !!leftHistory;
      const rightHasHistory = !!rightHistory;

      if (leftHasHistory !== rightHasHistory) {
        return leftHasHistory ? -1 : 1;
      }

      if (leftHasHistory && rightHasHistory) {
        const leftTime = leftHistory?.lastPerformedAt ? new Date(leftHistory.lastPerformedAt).getTime() : 0;
        const rightTime = rightHistory?.lastPerformedAt ? new Date(rightHistory.lastPerformedAt).getTime() : 0;
        if (leftTime !== rightTime) {
          return rightTime - leftTime;
        }
      }

      return left.name.localeCompare(right.name);
    });
  }, [results, historyMap]);

  const resetFilters = useCallback(() => {
    setSearch('');
    setFilterMuscle('');
    setFilterEquipment('');
    setFilterMovementPattern('');
    setSelected(null);
  }, []);

  const handleConfirm = async () => {
    if (!selected) return;
    try {
      setConfirming(true);
      await onConfirm(selected);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {description}
          </Typography>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            label="Търси по име"
            fullWidth
            inputProps={{ 'data-testid': 'exercise-picker-search-input' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="exercise-picker-muscle-group-filter-label">Мускулна група</InputLabel>
              <Select
                labelId="exercise-picker-muscle-group-filter-label"
                label="Мускулна група"
                value={filterMuscle}
                onChange={(e) => setFilterMuscle(String(e.target.value))}
                SelectDisplayProps={{ 'data-testid': 'exercise-picker-muscle-group-filter' } as any}
              >
                <MenuItem value="">Всички</MenuItem>
                {muscleGroups.map((muscleGroup) => (
                  <MenuItem key={muscleGroup} value={muscleGroup}>
                    {muscleGroup}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="exercise-picker-equipment-filter-label">Оборудване</InputLabel>
              <Select
                labelId="exercise-picker-equipment-filter-label"
                label="Оборудване"
                value={filterEquipment}
                onChange={(e) => setFilterEquipment(String(e.target.value))}
                SelectDisplayProps={{ 'data-testid': 'exercise-picker-equipment-filter' } as any}
              >
                <MenuItem value="">Всичко</MenuItem>
                {equipmentOptions.map((equipment) => (
                  <MenuItem key={equipment} value={equipment}>
                    {equipment}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel id="exercise-picker-movement-pattern-filter-label">Movement pattern</InputLabel>
              <Select
                labelId="exercise-picker-movement-pattern-filter-label"
                label="Movement pattern"
                value={filterMovementPattern}
                onChange={(e) => setFilterMovementPattern(String(e.target.value))}
                SelectDisplayProps={{ 'data-testid': 'exercise-picker-movement-pattern-filter' } as any}
              >
                <MenuItem value="">Всички</MenuItem>
                {movementPatterns.map((pattern) => (
                  <MenuItem key={pattern} value={pattern}>
                    {pattern}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {loading && <LinearProgress />}
          {orderedExercises.length > 0 ? (
            <List
              dense
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 360, overflow: 'auto' }}
              data-testid="exercise-picker-results-list"
            >
              {orderedExercises.map((exercise) => {
                const historyEntry = historyMap.get(exercise.id);
                const isSelected = selected?.id === exercise.id;

                return (
                  <ListItem key={exercise.id} disablePadding>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => setSelected(exercise)}
                      data-testid={`exercise-picker-result-${exercise.id}`}
                    >
                      <ListItemText
                        primary={exercise.name}
                        secondary={[
                          exercise.muscleGroup,
                          exercise.equipment || 'без оборудване',
                          exercise.movementPattern || 'без pattern',
                          historyEntry?.lastPerformedAt
                            ? `Последно: ${new Date(historyEntry.lastPerformedAt).toLocaleDateString('bg-BG')}`
                            : 'Без история',
                        ].join(' • ')}
                      />
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
              <Typography sx={{ mb: 1 }}>Няма намерени упражнения</Typography>
              <Button onClick={resetFilters}>Изчисти филтрите</Button>
            </Paper>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Откажи</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selected || confirming}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExercisePickerDialog;
